import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  FormControl,
  Button,
  Text
} from '@chakra-ui/react';
import { MdCancel, MdCheck } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useTickets } from '@hooks/useTickets';
import DynamicFormField from '@components/DynamicFormField/DynamicFormField';
import { CALL_LOG_FORM_CONFIG } from '@views/OutboundCallingDashboard/utils';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE, ROLES } from '@constants';
import useAuthRole from '@hooks/useAuthRole';
import { MAX_NOTE_LENGTH } from '@views/OutboundCallingDashboard/constant';

/**
 * Action Button Component
 */
const ActionButton = ({
  onClick,
  isLoading,
  isDisabled,
  colorScheme,
  leftIcon,
  children
}) => (
  <Button
    variant="outline"
    colorScheme={colorScheme}
    size="sm"
    border="2px solid"
    borderRadius="xl"
    isDisabled={isDisabled}
    isLoading={isLoading}
    onClick={onClick}
    leftIcon={leftIcon}
  >
    {children}
  </Button>
);

/**
 * Call Log Form Component
 * Form to log new calls using unified update API
 */
const CallLogForm = ({ ticket, onTicketUpdate }) => {
  const dispatch = useDispatch();
  const { hasAnyAuthority } = useAuthRole();

  const { updateTicket, isUpdatingTicket } = useTickets();

  const [formData, setFormData] = useState({});

  useEffect(() => {
    const initialData = CALL_LOG_FORM_CONFIG.reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});
    setFormData(initialData);
  }, []);

  /**
   * Handle form field change
   */
  const handleInputChange = ({ target: { name, value, type, checked } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'checkbox'
          ? checked
          : type === 'number'
          ? Number(value)
          : value
    }));
  };

  /**
   * Handle form submit
   */
  const handleSubmit = async (e) => {
    e?.preventDefault();

    // Validate required fields
    const isValid = CALL_LOG_FORM_CONFIG.every((field) => {
      if (field.isRequired) {
        return (
          formData[field.name] !== undefined &&
          formData[field.name] !== null &&
          formData[field.name] !== ''
        );
      }
      return true;
    });

    if (!isValid) {
      dispatch(
        setAlert({
          title: 'Please fill all required fields',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }

    try {
      // Use unified update API with callAttempt (AxiosInterceptor handles snake_case conversion)
      await updateTicket(ticket.ticketId, {
        callAttempt: {
          callStatus: formData.outcome,
          agentRemarks: formData.notes.trim()
        }
      });

      // Clear form on success
      handleClear();

      // Refresh tickets list
      onTicketUpdate && onTicketUpdate();
    } catch (error) {
      console.error('Failed to log call:', error);
    }
  };

  /**
   * Clear form
   */
  const handleClear = () => {
    const initialData = CALL_LOG_FORM_CONFIG.reduce((acc, field) => {
      acc[field.name] = field.value;
      return acc;
    }, {});
    setFormData(initialData);
  };

  /**
   * Calculate remaining characters
   */
  const remainingChars = MAX_NOTE_LENGTH - (formData.notes?.length || 0);
  const isNotesLimitReached = remainingChars < 0;

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <FormControl>
        <VStack alignItems="stretch" spacing={4}>
          {CALL_LOG_FORM_CONFIG.map((field, index) => (
            <DynamicFormField
              key={index}
              fieldConfig={field}
              data={formData}
              handleChange={handleInputChange}
            />
          ))}

          {/* Character count for notes */}
          <Text
            fontSize="xs"
            color={isNotesLimitReached ? 'red.400' : 'gray.500'}
            textAlign="right"
            mt={-2}
          >
            {remainingChars} characters remaining
          </Text>

          {/* Action Buttons */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <ActionButton
              onClick={handleClear}
              isDisabled={isUpdatingTicket}
              colorScheme="red"
              leftIcon={<MdCancel />}
            >
              Clear Form
            </ActionButton>
            {hasAnyAuthority(ROLES.OUTBOUND_CALLING_AGENT) && (
                <ActionButton
                  onClick={handleSubmit}
                  isLoading={isUpdatingTicket}
                  colorScheme="brand"
                  leftIcon={<MdCheck />}
                >
                  Save Call Log
                </ActionButton>
            )}
          </HStack>
        </VStack>
      </FormControl>
    </Box>
  );
};

export default CallLogForm;
